/**
 * CrewSpotlightRow ✦
 *
 * Public, de-identified live momentum row for the /crew landing.
 * Pulls /api/crew/spotlight and renders mini glassmorphic FlexCards
 * in a horizontally-scrolling, auto-drifting band. Each card shows
 * the crew member's first name + surname initial only — privacy
 * first, intrigue + authenticity by design.
 *
 * Subtle gold→pink rotating gradient halo on each card sells "this
 * place is alive" without being noisy. Auto-scroll pauses on hover.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Trophy, Sparkle, Lightning } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const STREAM_COPY = {
  shared: 'Just shared',
  unlocked: 'Just unlocked',
  xp: 'Levelling up',
};

const MiniFlexCard = ({ card }) => {
  const label = card.badge_label || (card.kind === 'level' ? `Level ${card.level}` : '✦');
  return (
    <div
      data-testid={`spotlight-card-${card.name.replace(/\s+/g, '-')}`}
      style={{
        flex: '0 0 auto', width: 200, scrollSnapAlign: 'start',
        borderRadius: 18, padding: 18, position: 'relative', overflow: 'hidden',
        background: `linear-gradient(180deg, ${PURPLE}33 0%, ${PINK}22 60%, ${BLUE}1A 100%),
                     rgba(15,15,32,0.85)`,
        border: `1px solid ${PINK}44`,
        boxShadow: `0 12px 32px rgba(0,0,0,0.45), 0 0 24px ${PURPLE}33`,
        backdropFilter: 'blur(10px)',
        color: TEXT, fontFamily: FONT_STACK,
        minHeight: 240,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      }}
    >
      {/* Subtle rotating halo behind the medallion */}
      <div style={{
        position: 'absolute', top: 18, left: 18, right: 18, height: 90,
        background: `radial-gradient(circle at 50% 50%, ${PINK}44 0%, transparent 70%)`,
        pointerEvents: 'none', filter: 'blur(8px)',
      }} />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 90, marginBottom: 12 }}>
        <div style={{
          width: 78, height: 78, borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%, ${PINK} 0%, ${PURPLE} 70%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 28px ${PINK}66, inset 0 -8px 12px rgba(0,0,0,0.25)`,
        }}>
          <Trophy size={36} weight="fill" color="#FFFFFF" />
        </div>
        <Sparkle size={14} weight="fill" color="#FFD166"
          style={{ position: 'absolute', top: 8, right: 28, filter: 'drop-shadow(0 0 6px #FFD166)' }} />
      </div>

      <div style={{ textAlign: 'center' }}>
        <p style={{
          margin: '0 0 4px', fontSize: 10.5, fontWeight: 800,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          background: `linear-gradient(135deg, ${BLUE}, ${PINK})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          ✦ {STREAM_COPY[card.stream] || 'Crew moment'}
        </p>
        <p style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 900, color: TEXT, letterSpacing: '-0.01em' }}>
          {label}
        </p>
        <p style={{ margin: '0', fontSize: 12.5, color: TEXT_MUTED }}>
          {card.name}
        </p>
      </div>

      <div style={{
        marginTop: 10, padding: '6px 10px', borderRadius: 999,
        background: 'rgba(255,255,255,0.06)', border: `1px solid ${BLUE}44`,
        fontSize: 11, fontWeight: 800, color: BLUE,
        display: 'inline-flex', alignItems: 'center', gap: 5,
        alignSelf: 'center',
      }}>
        <Lightning size={11} weight="fill" /> Level {card.level || 1}
      </div>
    </div>
  );
};

const CrewSpotlightRow = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/crew/spotlight?limit=12`);
        if (alive) setCards(r.data?.cards || []);
      } catch { /* silent — section just hides */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Auto-drift: nudge scrollLeft every 50ms; pause on hover/touch.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || cards.length === 0) return;
    let raf;
    let last = performance.now();
    const tick = (t) => {
      const dt = t - last;
      last = t;
      if (!pausedRef.current && !document.hidden) {
        el.scrollLeft += dt * 0.018; // ~18px/s drift
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) {
          el.scrollLeft = 0;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [cards.length]);

  if (loading || cards.length === 0) return null;

  return (
    <section
      data-testid="crew-spotlight"
      style={{ padding: 'clamp(40px, 6vw, 80px) 0' }}
    >
      <p style={{
        margin: '0 0 6px', fontFamily: FONT_STACK,
        fontSize: 11, fontWeight: 800, letterSpacing: '0.32em', textTransform: 'uppercase',
        background: `linear-gradient(135deg, ${PINK}, ${BLUE})`,
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>
        ✦ Live momentum
      </p>
      <h2 style={{
        margin: '8px 0 6px', fontFamily: FONT_STACK,
        fontWeight: 900, fontSize: 'clamp(26px, 4vw, 42px)',
        letterSpacing: '-0.02em', lineHeight: 1.1, color: TEXT,
      }}>
        Crew Members Levelling Up ✦
      </h2>
      <p style={{
        margin: '0 0 20px', maxWidth: 580, fontSize: 16,
        color: TEXT_MUTED, lineHeight: 1.55,
      }}>
        Real young people building confidence through events.
      </p>

      <div
        ref={trackRef}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
        onTouchStart={() => { pausedRef.current = true; }}
        onTouchEnd={() => { setTimeout(() => { pausedRef.current = false; }, 2400); }}
        data-testid="spotlight-track"
        style={{
          display: 'flex', gap: 14, overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          paddingBottom: 10, paddingTop: 2,
          scrollbarWidth: 'none',
          WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 5%, #000 95%, transparent 100%)',
          maskImage: 'linear-gradient(90deg, transparent 0, #000 5%, #000 95%, transparent 100%)',
        }}
      >
        {/* Render the list TWICE for a seamless drift loop */}
        {[...cards, ...cards].map((c, i) => (
          <MiniFlexCard key={`${c.name}-${c.stream}-${i}`} card={c} />
        ))}
      </div>

      {/* Hide scrollbar in webkit */}
      <style>{`
        [data-testid="spotlight-track"]::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
};

export default CrewSpotlightRow;
